import { useMemo, useState } from 'react'

import { Field } from '../../models/Field'
import Button from '../app/design/Button'
import EmptyState from '../app/design/EmptyState'
import Modal from '../app/design/Modal'
import { CustomFieldEditor } from './CustomFieldEditor'

type Props = {
    isOpen: boolean
    onClose: () => void
    title?: string
    fields: Field[]
    answers: Record<string, any>
    onUpdateAnswers: (newAnswers: Record<string, any>) => void
    shouldEnforceRequired: boolean
}

const CustomFieldRecordComponent = ({
    isOpen,
    onClose,
    title,
    fields,
    answers: defaultAnswers,
    onUpdateAnswers,
    shouldEnforceRequired,
}: Props) => {
    const [answers, setAnswers] = useState(defaultAnswers)

    const areAnswersValid = useMemo(() => {
        if (!shouldEnforceRequired) {
            return true
        }

        return fields
            .filter(
                (field) => field.required && field.type !== 'image' && field.type !== 'document'
            )
            .every((field) => {
                if (field.type === 'multiselect') {
                    const values = answers[field.name ?? '']
                    return !!values && values.length > 0
                }
                else {
                    return !!answers[field.name ?? '']
                }
            })
    }, [answers, shouldEnforceRequired])

    const handleDone = () => {
        if (areAnswersValid) {
            onUpdateAnswers(answers)
        }
        onClose()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            footer={
                <Button intent="primary" onClick={handleDone} disabled={!areAnswersValid}>
                    Done
                </Button>
            }
        >
            <div className="flex flex-col items-stretch justify-start gap-y-8">
                {fields.length > 0 ? (
                    fields
                        .filter((field) => field.type !== 'image' && field.type !== 'document')
                        .map((field) => (
                            <CustomFieldEditor
                                key={field.id ?? ''}
                                field={field}
                                value={answers[field.name ?? '']}
                                onChange={(newValue) =>
                                    setAnswers((previousAnswers) => ({
                                        ...previousAnswers,
                                        [field.name ?? '']: newValue,
                                    }))
                                }
                                shouldEnforceRequired={shouldEnforceRequired}
                            />
                        ))
                ) : (
                    <EmptyState title="No fields" message="You haven't added any fields yet" />
                )}
            </div>
        </Modal>
    )
}

// This wrapper exists to key the main component on `isOpen`, fully resetting
// state whenever the modal is toggled.
export const CustomFieldRecordModal = (props: Props) => {
    return <CustomFieldRecordComponent {...props} key={props.isOpen.toString()} />
}
